import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../../lib/supabase';
import { usePdf, usePdfSignedUrl } from '../../../../queries/pdfs';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import SpeakerNotesPanel from './components/SpeakerNotesPanel';
import TimerDisplay from '../../../../components/remote/TimerDisplay';
import PointerModeBar, { type PointerModeOption } from '../../../../components/remote/PointerModeBar';
import PageNavigator from '../../../../components/remote/PageNavigator';
import SketchToolbar from '../../../../components/remote/SketchToolbar';
import EventModeButtons, { type EventModeItem } from '../../../../components/remote/EventModeButtons';
import RemoteTabs from '../../../../components/remote/RemoteTabs';
import SliderField from '../../../../components/remote/SliderField';
import RemoteTouchpad from '../../../../components/remote/RemoteTouchpad';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface SketchPoint {
  x: number;
  y: number;
}

interface SketchStroke {
  points: SketchPoint[];
  color: string;
  width: number;
}

type PointerMode = 'laser' | 'highlight' | 'sketch' | 'magnifier';

export default function PresentationRemote() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [authChecked, setAuthChecked] = useState(false);
  const { data: initialPdf, isLoading: pdfQueryLoading } = usePdf(authChecked ? (id ?? null) : null);
  const { data: signedUrl } = usePdfSignedUrl(authChecked ? (id ?? null) : null, initialPdf?.file_path ?? null);
  const [loading, setLoading] = useState(true);
  const [pdfData, setPdfData] = useState<any>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);

  // 포인터 상태
  const [pointerMode, setPointerMode] = useState<PointerMode>('laser');
  const [laserPointer, setLaserPointer] = useState({ x: 0, y: 0, visible: false });
  const [highlightPointer, setHighlightPointer] = useState({ x: 0, y: 0, visible: false });
  const [magnifierPointer, setMagnifierPointer] = useState({ x: 0, y: 0, visible: false, scale: 2 });
  const [highlightRadius, setHighlightRadius] = useState<number>(15);
  const [magnifierScale, setMagnifierScale] = useState<number>(2);
  const [showSettings, setShowSettings] = useState<PointerMode | null>(null);

  // 스케치 상태
  const [sketchStrokes, setSketchStrokes] = useState<SketchStroke[]>([]);
  const [currentStroke, setCurrentStroke] = useState<SketchPoint[]>([]);
  const [sketchColor, setSketchColor] = useState<string>('#FF0000');
  const [sketchWidth, setSketchWidth] = useState<number>(4);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const mobileSketchCanvasRef = useRef<HTMLCanvasElement>(null);

  // 타이머 상태
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [isTimerRunning, setIsTimerRunning] = useState<boolean>(false);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 이벤트 상태
  const [activeEventNumber, setActiveEventNumber] = useState<number | null>(null);

  // 탭 상태 (컨트롤 / 노트)
  const [activeTab, setActiveTab] = useState<'controls' | 'notes'>('controls');

  const laserChannelRef = useRef<any>(null);
  const laserTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const magnifierTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentPageRef = useRef<number>(1);
  const lastUpdateTime = useRef<number>(0);
  const isLocalUpdate = useRef<boolean>(false);

  // 인증 확인 (Supabase auth 또는 QR 세션)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();

        if (authUser) {
          setAuthChecked(true);
          return;
        }

        // QR 세션 확인
        const sessionToken = localStorage.getItem('qr_session_token');
        const qrUserId = localStorage.getItem('qr_user_id');

        if (sessionToken && qrUserId) {
          const { data: session, error } = await supabase
            .from('qr_sessions')
            .select('*')
            .eq('session_token', sessionToken)
            .eq('user_id', qrUserId)
            .eq('is_used', true)
            .maybeSingle();

          if (!error && session) {
            const expiresAt = new Date(session.expires_at);
            if (expiresAt > new Date()) {
              setAuthChecked(true);
              return;
            }
          }
        }

        navigate('/login');
      } catch {
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate]);

  // PDF 데이터 + URL이 준비되면 로컬 state 초기화
  useEffect(() => {
    if (initialPdf && signedUrl && !pdfData) {
      setPdfData(initialPdf);
      setPageNumber(initialPdf.current_page || 1);
      currentPageRef.current = initialPdf.current_page || 1;
      setNumPages(initialPdf.page_count || 0);
      setPdfUrl(signedUrl);
      setLoading(false);
    }
    if (!pdfQueryLoading && !initialPdf && authChecked && id) {
      navigate('/dashboard');
    }
  }, [initialPdf, signedUrl, pdfData, pdfQueryLoading, authChecked, id, navigate]);

  // 리모컨 진입 시 타이머 자동 시작
  useEffect(() => {
    if (!loading && pdfUrl) {
      setElapsedTime(0);
      setIsTimerRunning(true);
      timerIntervalRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [loading, pdfUrl]);

  // 포인터 채널 설정 (SEND only)
  useEffect(() => {
    if (!pdfData) return;

    if (laserChannelRef.current) {
      supabase.removeChannel(laserChannelRef.current);
      laserChannelRef.current = null;
    }

    const channel = supabase.channel(`pointer:${pdfData.id}`, {
      config: { broadcast: { self: false } },
    });

    channel.subscribe();
    laserChannelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      laserChannelRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfData?.id]);

  const startTimer = () => {
    if (timerIntervalRef.current) return;
    setIsTimerRunning(true);
    timerIntervalRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
  };

  const pauseTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    setIsTimerRunning(false);
  };

  const resetTimer = () => {
    if (!window.confirm(t('remote.reset_confirm'))) return;
    pauseTimer();
    setElapsedTime(0);
  };

  // 페이지 변경
  const handlePageChange = async (newPage: number) => {
    if (!pdfData || newPage < 1 || newPage > numPages) return;

    // 페이지 변경 시 스케치 초기화
    setSketchStrokes([]);
    setCurrentStroke([]);
    await supabase
      .from('pdf_files')
      .update({ sketch_data: [], sketch_timestamp: Date.now(), sketch_clear: true })
      .eq('id', pdfData.id);

    setPageNumber(newPage);
    currentPageRef.current = newPage;

    // 실시간 채널로 즉시 브로드캐스트
    laserChannelRef.current?.send({
      type: 'broadcast',
      event: 'page_change',
      payload: { page: newPage },
    });

    lastUpdateTime.current = Date.now();
    isLocalUpdate.current = true;

    try {
      await supabase
        .from('pdf_files')
        .update({ current_page: newPage, updated_at: new Date().toISOString() })
        .eq('id', pdfData.id);
      lastUpdateTime.current = Date.now();
    } catch (err) {
      console.error('페이지 업데이트 오류:', err);
    } finally {
      setTimeout(() => { isLocalUpdate.current = false; }, 300);
    }
  };

  // 레이저 포인터
  const updateLaserPointer = (x: number, y: number, visible: boolean) => {
    if (!pdfData) return;
    const timestamp = Date.now();
    setLaserPointer({ x, y, visible });
    laserChannelRef.current?.send({
      type: 'broadcast',
      event: 'laser',
      payload: { x, y, visible, timestamp },
    });
  };

  const handleMobileLaserTouch = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!pdfData) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const touch = e.touches[0];
    const x = ((touch.clientX - rect.left) / rect.width) * 100;
    const y = ((touch.clientY - rect.top) / rect.height) * 100;
    updateLaserPointer(x, y, true);
    if (laserTimeoutRef.current) { clearTimeout(laserTimeoutRef.current); laserTimeoutRef.current = null; }
  };

  const handleMobileLaserTouchEnd = () => {
    if (laserTimeoutRef.current) { clearTimeout(laserTimeoutRef.current); laserTimeoutRef.current = null; }
    updateLaserPointer(0, 0, false);
  };

  // 하이라이트 포인터
  const updateHighlightPointer = (x: number, y: number, visible: boolean) => {
    if (!pdfData) return;
    const timestamp = Date.now();
    setHighlightPointer({ x, y, visible });
    laserChannelRef.current?.send({
      type: 'broadcast',
      event: 'highlight',
      payload: { x, y, radius: highlightRadius, visible, timestamp },
    });
  };

  const handleMobileHighlightTouch = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!pdfData) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const touch = e.touches[0];
    const x = ((touch.clientX - rect.left) / rect.width) * 100;
    const y = ((touch.clientY - rect.top) / rect.height) * 100;
    updateHighlightPointer(x, y, true);
    if (highlightTimeoutRef.current) { clearTimeout(highlightTimeoutRef.current); highlightTimeoutRef.current = null; }
  };

  const handleMobileHighlightTouchEnd = () => {
    if (highlightTimeoutRef.current) { clearTimeout(highlightTimeoutRef.current); highlightTimeoutRef.current = null; }
    updateHighlightPointer(0, 0, false);
  };

  // 돋보기 포인터
  const updateMagnifierPointer = (x: number, y: number, visible: boolean) => {
    if (!pdfData) return;
    const timestamp = Date.now();
    setMagnifierPointer({ x, y, visible, scale: magnifierScale });
    laserChannelRef.current?.send({
      type: 'broadcast',
      event: 'magnifier',
      payload: { x, y, scale: magnifierScale, visible, timestamp },
    });
  };

  const handleMobileMagnifierTouch = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!pdfData) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const touch = e.touches[0];
    const x = ((touch.clientX - rect.left) / rect.width) * 100;
    const y = ((touch.clientY - rect.top) / rect.height) * 100;
    updateMagnifierPointer(x, y, true);
    if (magnifierTimeoutRef.current) { clearTimeout(magnifierTimeoutRef.current); magnifierTimeoutRef.current = null; }
  };

  const handleMobileMagnifierTouchEnd = () => {
    if (magnifierTimeoutRef.current) { clearTimeout(magnifierTimeoutRef.current); magnifierTimeoutRef.current = null; }
    updateMagnifierPointer(0, 0, false);
  };

  // 스케치 캔버스 그리기
  const drawSketchOnCanvas = (
    canvas: HTMLCanvasElement | null,
    strokes: SketchStroke[],
    currentPoints: SketchPoint[],
    currentColor: string,
    currentWidthVal: number
  ) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    ctx.clearRect(0, 0, rect.width, rect.height);

    strokes.forEach(stroke => {
      if (stroke.points.length < 2) return;
      ctx.beginPath();
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const firstPoint = stroke.points[0];
      ctx.moveTo((firstPoint.x / 100) * rect.width, (firstPoint.y / 100) * rect.height);
      for (let i = 1; i < stroke.points.length; i++) {
        const point = stroke.points[i];
        ctx.lineTo((point.x / 100) * rect.width, (point.y / 100) * rect.height);
      }
      ctx.stroke();
    });

    if (currentPoints.length >= 2) {
      ctx.beginPath();
      ctx.strokeStyle = currentColor;
      ctx.lineWidth = currentWidthVal;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const firstPoint = currentPoints[0];
      ctx.moveTo((firstPoint.x / 100) * rect.width, (firstPoint.y / 100) * rect.height);
      for (let i = 1; i < currentPoints.length; i++) {
        const point = currentPoints[i];
        ctx.lineTo((point.x / 100) * rect.width, (point.y / 100) * rect.height);
      }
      ctx.stroke();
    }
  };

  useEffect(() => {
    if (pointerMode === 'sketch' && mobileSketchCanvasRef.current) {
      drawSketchOnCanvas(mobileSketchCanvasRef.current, sketchStrokes, currentStroke, sketchColor, sketchWidth);
    }
  }, [sketchStrokes, currentStroke, sketchColor, sketchWidth, pointerMode]);

  const handleMobileSketchTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!pdfData) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const touch = e.touches[0];
    const x = ((touch.clientX - rect.left) / rect.width) * 100;
    const y = ((touch.clientY - rect.top) / rect.height) * 100;
    setIsDrawing(true);
    setCurrentStroke([{ x, y }]);
  };

  const handleMobileSketchTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDrawing || !pdfData) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const touch = e.touches[0];
    const x = ((touch.clientX - rect.left) / rect.width) * 100;
    const y = ((touch.clientY - rect.top) / rect.height) * 100;
    setCurrentStroke(prev => [...prev, { x, y }]);
  };

  const handleMobileSketchTouchEnd = () => {
    if (!isDrawing || currentStroke.length === 0) { setIsDrawing(false); return; }

    const newStroke: SketchStroke = { points: currentStroke, color: sketchColor, width: sketchWidth };
    const newStrokes = [...sketchStrokes, newStroke];
    setSketchStrokes(newStrokes);
    setCurrentStroke([]);
    setIsDrawing(false);

    // DB 및 채널로 전송
    if (pdfData) {
      supabase.from('pdf_files')
        .update({ sketch_data: newStrokes, sketch_timestamp: Date.now(), sketch_clear: false })
        .eq('id', pdfData.id)
        .then(() => {});
      laserChannelRef.current?.send({
        type: 'broadcast',
        event: 'sketch',
        payload: { strokes: newStrokes, clear: false },
      });
    }
  };

  const clearSketch = async () => {
    setSketchStrokes([]);
    setCurrentStroke([]);
    if (!pdfData) return;
    await supabase.from('pdf_files')
      .update({ sketch_data: [], sketch_timestamp: Date.now(), sketch_clear: true })
      .eq('id', pdfData.id);
    laserChannelRef.current?.send({
      type: 'broadcast',
      event: 'sketch',
      payload: { strokes: [], clear: true },
    });
  };

  // 이벤트 트리거
  const triggerEvent = async (eventNumber: number, url: string) => {
    if (!pdfData) return;
    if (activeEventNumber === eventNumber) {
      setActiveEventNumber(null);
      await supabase.from('pdf_files')
        .update({ event_visible: false, event_active: false, event_timestamp: Date.now() })
        .eq('id', pdfData.id);
      return;
    }
    const timestamp = Date.now();
    setActiveEventNumber(eventNumber);
    await supabase.from('pdf_files')
      .update({ event_url: url, event_visible: true, event_timestamp: timestamp, event_active: true, event_number: eventNumber })
      .eq('id', pdfData.id);
  };

  // 로그아웃
  const handleLogout = async () => {
    const sessionToken = localStorage.getItem('qr_session_token');
    const userId = localStorage.getItem('qr_user_id');
    if (sessionToken) {
      await supabase.from('qr_sessions')
        .update({ logout_requested: true, logout_timestamp: new Date().toISOString() })
        .eq('session_token', sessionToken);
    }
    if (userId) {
      await supabase.from('qr_sessions')
        .update({ logout_requested: true, logout_timestamp: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('is_used', true);
    }
    await supabase.auth.signOut();
    localStorage.removeItem('qr_session_token');
    localStorage.removeItem('qr_user_id');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-gray-50">
      {/* 상단 헤더 */}
      <header className="bg-white px-4 py-3 flex items-center justify-between flex-shrink-0 border-b border-gray-200">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-all cursor-pointer"
        >
          <i className="ri-dashboard-line"></i>
          {t('common.dashboard')}
        </button>
        <button
          onClick={handleLogout}
          className="flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer flex-shrink-0"
        >
          <i className="ri-logout-box-r-line"></i>
        </button>
      </header>

      {/* 리모컨 콘텐츠 */}
      <div className="flex-1 overflow-auto p-4 flex flex-col">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex-1 flex flex-col overflow-hidden p-3">
          {/* 숨겨진 PDF Document (페이지 수 로드용) */}
          {pdfUrl && (
            <div className="hidden">
              <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              >
                <Page pageNumber={1} width={100} renderTextLayer={false} renderAnnotationLayer={false} />
              </Document>
            </div>
          )}

          {/* 발표 타이머 */}
          <div className="mb-3">
            <TimerDisplay
              elapsedSeconds={elapsedTime}
              isRunning={isTimerRunning}
              onToggle={isTimerRunning ? pauseTimer : startTimer}
              onReset={resetTimer}
            />
          </div>

          {/* 포인터 모드 선택 */}
          <div className="mb-2 flex-shrink-0">
            <p className="font-medium text-gray-700 text-xs mb-2">{t('remote.pointer_mode')}</p>
            <PointerModeBar
              options={[
                { mode: 'laser', label: t('remote.laser'), icon: 'ri-focus-3-line', activeClass: 'bg-red-500' },
                { mode: 'highlight', label: t('remote.highlight'), icon: 'ri-flashlight-line', activeClass: 'bg-amber-500' },
                { mode: 'sketch', label: t('remote.sketch'), icon: 'ri-pencil-line', activeClass: 'bg-emerald-500' },
                { mode: 'magnifier', label: t('remote.magnifier'), icon: 'ri-search-line', activeClass: 'bg-blue-500' },
              ] satisfies PointerModeOption[]}
              value={pointerMode}
              onSelect={(mode) => {
                if (pointerMode === mode) {
                  if (mode === 'sketch') {
                    setShowSettings('sketch');
                  } else {
                    setShowSettings(showSettings === mode ? null : mode);
                  }
                } else {
                  setPointerMode(mode);
                  setShowSettings(mode === 'sketch' ? 'sketch' : null);
                }
              }}
            />
          </div>

          <EventModeButtons
            title={t('remote.event_mode')}
            activeEventNumber={activeEventNumber}
            items={[
              {
                number: 1,
                iconActive: 'ri-stop-circle-line',
                iconIdle: 'ri-fire-line',
                activeLabel: t('remote.event_off', { number: 1 }),
                idleLabel: t('remote.event_on', { number: 1 }),
                url: 'https://gyktnm.readdy.co',
              },
              {
                number: 2,
                iconActive: 'ri-stop-circle-line',
                iconIdle: 'ri-star-line',
                disabled: true,
                activeLabel: t('remote.event_off', { number: 2 }),
                idleLabel: t('remote.event_on', { number: 2 }),
              },
              {
                number: 3,
                iconActive: 'ri-stop-circle-line',
                iconIdle: 'ri-gift-line',
                disabled: true,
                activeLabel: t('remote.event_off', { number: 3 }),
                idleLabel: t('remote.event_on', { number: 3 }),
              },
              {
                number: 4,
                iconActive: 'ri-stop-circle-line',
                iconIdle: 'ri-trophy-line',
                disabled: true,
                activeLabel: t('remote.event_off', { number: 4 }),
                idleLabel: t('remote.event_on', { number: 4 }),
              },
            ] satisfies EventModeItem[]}
            onTrigger={(item) => triggerEvent(item.number, item.url ?? '')}
          />

          <RemoteTabs
            value={activeTab}
            onChange={setActiveTab}
            controlsLabel={t('remote.controls')}
            notesLabel={t('remote.notes')}
          />

          {/* 노트 탭 */}
          {activeTab === 'notes' && (
            <SpeakerNotesPanel pdfId={id} />
          )}

          {/* 스크롤 가능한 컨트롤 영역 */}
          {activeTab === 'controls' && <div className="flex-1 flex flex-col min-h-0 overflow-auto">
            {/* 레이저 포인터 컨트롤 */}
            {pointerMode === 'laser' && (
              <div className="flex flex-col h-full">
                <div className="flex-1 flex flex-col justify-end">
                  <div className="mb-2 flex-shrink-0">
                    <PageNavigator
                      current={pageNumber}
                      total={numPages}
                      onPrev={() => handlePageChange(pageNumber - 1)}
                      onNext={() => handlePageChange(pageNumber + 1)}
                      prevLabel={t('common.previous')}
                      nextLabel={t('common.next')}
                    />
                  </div>
                  <RemoteTouchpad
                    borderActiveClass="active:border-red-400"
                    hintIconClass="ri-focus-3-line"
                    hintText={t('remote.touch_laser')}
                    showHint={!laserPointer.visible}
                    onTouchStart={handleMobileLaserTouch}
                    onTouchMove={handleMobileLaserTouch}
                    onTouchEnd={handleMobileLaserTouchEnd}
                    pdfSlot={
                      pdfUrl && (
                        <Document file={pdfUrl} loading={null} error={null}>
                          <Page pageNumber={pageNumber} width={window.innerWidth - 56} renderTextLayer={false} renderAnnotationLayer={false} className="max-h-full" />
                        </Document>
                      )
                    }
                  >
                    {laserPointer.visible && (
                      <div className="absolute w-5 h-5 bg-red-500 rounded-full shadow-[0_0_12px_5px_rgba(239,68,68,0.6)] pointer-events-none z-10"
                        style={{ left: `${laserPointer.x}%`, top: `${laserPointer.y}%`, transform: 'translate(-50%, -50%)' }}>
                      </div>
                    )}
                  </RemoteTouchpad>
                </div>
              </div>
            )}

            {/* 하이라이트 포인터 컨트롤 */}
            {pointerMode === 'highlight' && (
              <div className="flex flex-col h-full">
                {showSettings === 'highlight' && (
                  <SliderField
                    label={t('remote.highlight_size')}
                    value={highlightRadius}
                    min={5}
                    max={30}
                    valueLabel={`${highlightRadius}%`}
                    accentColorClass="accent-amber-500"
                    onChange={setHighlightRadius}
                  />
                )}
                <div className="flex-1 flex flex-col justify-end">
                  <div className="mb-2 flex-shrink-0">
                    <PageNavigator
                      current={pageNumber}
                      total={numPages}
                      onPrev={() => handlePageChange(pageNumber - 1)}
                      onNext={() => handlePageChange(pageNumber + 1)}
                      prevLabel={t('common.previous')}
                      nextLabel={t('common.next')}
                    />
                  </div>
                  <RemoteTouchpad
                    borderActiveClass="active:border-amber-400"
                    hintIconClass="ri-flashlight-line"
                    hintText={t('remote.touch_highlight')}
                    showHint={!highlightPointer.visible}
                    onTouchStart={handleMobileHighlightTouch}
                    onTouchMove={handleMobileHighlightTouch}
                    onTouchEnd={handleMobileHighlightTouchEnd}
                    pdfSlot={
                      pdfUrl && (
                        <Document file={pdfUrl} loading={null} error={null}>
                          <Page pageNumber={pageNumber} width={window.innerWidth - 56} renderTextLayer={false} renderAnnotationLayer={false} className="max-h-full" />
                        </Document>
                      )
                    }
                  >
                    {highlightPointer.visible && (
                      <svg width="100%" height="100%" className="absolute inset-0 pointer-events-none z-10">
                        <defs>
                          <mask id="mobileHighlightMask">
                            <rect width="100%" height="100%" fill="white" />
                            <circle cx={`${highlightPointer.x}%`} cy={`${highlightPointer.y}%`} r={`${highlightRadius * 2}px`} fill="black" />
                          </mask>
                        </defs>
                        <rect width="100%" height="100%" fill="rgba(0, 0, 0, 0.6)" mask="url(#mobileHighlightMask)" />
                        <circle cx={`${highlightPointer.x}%`} cy={`${highlightPointer.y}%`} r={`${highlightRadius * 2}px`} fill="none" stroke="rgba(255, 255, 255, 0.5)" strokeWidth="2" />
                      </svg>
                    )}
                  </RemoteTouchpad>
                </div>
              </div>
            )}

            {/* 스케치 모드 컨트롤 */}
            {pointerMode === 'sketch' && (
              <div className="flex flex-col h-full">
                {showSettings === 'sketch' && (
                  <SketchToolbar
                    color={sketchColor}
                    width={sketchWidth}
                    colors={['#FF0000', '#FF6B00', '#FFD700', '#00C853', '#2196F3', '#9C27B0', '#FFFFFF', '#000000']}
                    settingsLabel={t('remote.sketch_settings')}
                    clearLabel={t('remote.clear_all')}
                    colorLabel={t('remote.pen_color')}
                    widthLabel={t('remote.pen_width')}
                    onChangeColor={setSketchColor}
                    onChangeWidth={setSketchWidth}
                    onClear={clearSketch}
                  />
                )}
                <div className="flex-1 flex flex-col justify-end">
                  <div className="mb-2 flex-shrink-0">
                    <PageNavigator
                      current={pageNumber}
                      total={numPages}
                      onPrev={() => handlePageChange(pageNumber - 1)}
                      onNext={() => handlePageChange(pageNumber + 1)}
                      prevLabel={t('common.previous')}
                      nextLabel={t('common.next')}
                    />
                  </div>
                  <RemoteTouchpad
                    borderActiveClass="active:border-emerald-400"
                    hintIconClass="ri-pencil-line"
                    hintText={t('remote.touch_draw')}
                    showHint={sketchStrokes.length === 0 && !isDrawing}
                    onTouchStart={handleMobileSketchTouchStart}
                    onTouchMove={handleMobileSketchTouchMove}
                    onTouchEnd={handleMobileSketchTouchEnd}
                    pdfSlot={
                      pdfUrl && (
                        <Document file={pdfUrl} loading={null} error={null}>
                          <Page pageNumber={pageNumber} width={window.innerWidth - 56} renderTextLayer={false} renderAnnotationLayer={false} className="max-h-full" />
                        </Document>
                      )
                    }
                  >
                    <canvas ref={mobileSketchCanvasRef} className="absolute inset-0 w-full h-full z-10" style={{ touchAction: 'none' }} />
                  </RemoteTouchpad>
                </div>
              </div>
            )}

            {/* 돋보기 모드 컨트롤 */}
            {pointerMode === 'magnifier' && (
              <div className="flex flex-col h-full">
                {showSettings === 'magnifier' && (
                  <SliderField
                    label={t('remote.zoom_level')}
                    value={magnifierScale}
                    min={1.5}
                    max={3}
                    step={0.1}
                    valueLabel={t('common.magnification', { count: magnifierScale })}
                    accentColorClass="accent-blue-500"
                    onChange={setMagnifierScale}
                  />
                )}
                <div className="flex-1 flex flex-col justify-end">
                  <div className="mb-2 flex-shrink-0">
                    <PageNavigator
                      current={pageNumber}
                      total={numPages}
                      onPrev={() => handlePageChange(pageNumber - 1)}
                      onNext={() => handlePageChange(pageNumber + 1)}
                      prevLabel={t('common.previous')}
                      nextLabel={t('common.next')}
                    />
                  </div>
                  <RemoteTouchpad
                    borderActiveClass="active:border-blue-400"
                    hintIconClass="ri-search-line"
                    hintText={t('remote.touch_zoom')}
                    showHint={!magnifierPointer.visible}
                    onTouchStart={handleMobileMagnifierTouch}
                    onTouchMove={handleMobileMagnifierTouch}
                    onTouchEnd={handleMobileMagnifierTouchEnd}
                    pdfSlot={
                      pdfUrl && (
                        <Document file={pdfUrl} loading={null} error={null}>
                          <Page pageNumber={pageNumber} width={window.innerWidth - 56} renderTextLayer={false} renderAnnotationLayer={false} className="max-h-full" />
                        </Document>
                      )
                    }
                  >
                    {magnifierPointer.visible && (
                      <div className="absolute pointer-events-none z-10"
                        style={{ left: `${magnifierPointer.x}%`, top: `${magnifierPointer.y}%`, transform: 'translate(-50%, -50%)' }}>
                        <div className="rounded-full border-4 border-blue-400 shadow-2xl overflow-hidden bg-white relative" style={{ width: '80px', height: '80px' }}>
                          <div className="absolute inset-0" style={{ overflow: 'hidden' }}>
                            <div style={{
                              position: 'absolute',
                              width: `${(window.innerWidth - 56) * magnifierPointer.scale}px`,
                              height: `${((window.innerWidth - 56) * 9 / 16) * magnifierPointer.scale}px`,
                              left: `${40 - (magnifierPointer.x * (window.innerWidth - 56) * magnifierPointer.scale / 100)}px`,
                              top: `${40 - (magnifierPointer.y * ((window.innerWidth - 56) * 9 / 16) * magnifierPointer.scale / 100)}px`,
                            }}>
                              {pdfUrl && (
                                <Document file={pdfUrl} loading={null} error={null}>
                                  <Page pageNumber={pageNumber} width={(window.innerWidth - 56) * magnifierPointer.scale} renderTextLayer={false} renderAnnotationLayer={false} />
                                </Document>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="absolute top-1/2 left-1/2 w-1.5 h-1.5 bg-blue-500 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                      </div>
                    )}
                  </RemoteTouchpad>
                </div>
              </div>
            )}
          </div>}
        </div>
      </div>
    </div>
  );
}
